import React, { useState } from 'react';
import {
  ContainerSubTable,
  SubTable,
  SubTableBody,
  SubTableHeader,
  SubTableRow
} from './styled';
import TextHelper from '@/core/helpers/TextHelper';
import { Block, Tag } from '@/presentation/components';
import Spinner from '@/presentation/components/Spinner';
import ReusableButton from '@/presentation/reusables/Button';
import { CardProps, Detail } from '../../interfaces/propsCourseSchedule';
import EmptyDataStateMessage from '@/presentation/reusables/EmptyDataStateMessage';

const formatSchedulesDays = (schedulesSelected: Detail[]) =>
  schedulesSelected
    ?.map((detail: any) =>
      detail.schedules?.map((h: any) => {
        const hStart = Number(h.start.split(':')[0]);
        const hEnd = Number(h.end.split(':')[0]);
        const formattedStart =
          hStart < 12 ? `${h.start} a.m` : `${h.start} p.m`;
        const formattedEnd = hEnd < 12 ? `${h.end} a.m` : `${h.end} p.m`;
        return {
          ...h,
          start: formattedStart,
          end: formattedEnd,
          courseId: detail.courseId,
          section: detail.section
        };
      })
    )
    .flat();

const typeConfig: Record<
  string,
  { bg: string; width: number; color: string; text: string }
> = {
  P: { bg: '#bf500b', width: 79, color: '#ffffff', text: 'Presencial' },
  R: { bg: '#0f6778', width: 102, color: '#b3ebfe', text: 'Virtual en vivo' },
  VT: { bg: '#b21f5f', width: 90, color: '#ffffff', text: 'Virtual 24/7' }
};

const getTypeConfig = (type: string) =>
  typeConfig[type] || { bg: '#fff', width: 102, color: 'black', text: '-' };

const renderSchedules = (schedule: Detail, formattedSchedules: any) => {
  if (schedule.typeCourse === 'VT') {
    return <div>Disponible las 24 horas durante el ciclo</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {formattedSchedules
        ?.filter(
          (h: any) =>
            h.section === schedule.section && h.courseId === schedule.courseId
        )
        .map((h: any, i: number) => (
          <span key={`${h.day}-${h.start}-${h.end}-${i}`}>
            {h.day} {h.start} - {h.end}
          </span>
        ))}
    </div>
  );
};

const renderTeachers = (teachers: string[], isMobile?: boolean) =>
  teachers?.map((teacher, i) => {
    const preprocessedTeacher = teacher
      .split(',')
      .map((namePart: string) => TextHelper.capitalize(namePart.trim()))
      .join(', ');
    if (isMobile) {
      return (
        <div key={`teacher-${teacher}-${i}`} style={{ marginTop: 0 }}>
          {i === 0 && 'Docente: '}
          {preprocessedTeacher}
        </div>
      );
    }
    return <div key={`teacher-${teacher}-${i}`}>{preprocessedTeacher}</div>;
  });

const ScheduleRow = ({
  schedule,
  formatSchedules
}: {
  schedule: Detail;
  formatSchedules: any;
}) => (
  <SubTableRow>
    <div>{schedule.section}</div>
    <div>{renderSchedules(schedule, formatSchedules)}</div>
    <div>{renderTeachers(schedule.teachers)}</div>
    <Block flex className="headerLabel">
      <Tag
        backgroundColor={getTypeConfig(schedule.typeCourse).bg}
        width={getTypeConfig(schedule.typeCourse).width}
        color={getTypeConfig(schedule.typeCourse).color}
        padding="4px 12px 4px 12px"
        radius={20}
        lineHeight={16}
        size={12}
        height={24}
        bold
        gap={10}
      >
        {getTypeConfig(schedule.typeCourse).text}
      </Tag>
    </Block>
  </SubTableRow>
);

const LoadMoreRow = ({
  onClick,
  hasMore
}: {
  onClick: () => void;
  hasMore: boolean;
}) => (
  <SubTableRow
    style={{
      display: 'flex',
      width: 'auto',
      justifyContent: 'center',
      alignItems: 'center'
    }}
  >
    <ReusableButton
      data-testid="show-schedules-button"
      iconPrepend={hasMore ? 'chevron-down' : 'chevron-up'}
      onClick={onClick}
      outlined
      style={{
        width: hasMore ? 150 : 175,
        height: 32,
        gap: 4,
        border: 0,
        fontSize: 12
      }}
    >
      {hasMore ? 'Ver más secciones' : 'Ver menos secciones'}
    </ReusableButton>
  </SubTableRow>
);

const SubTableSchedules = ({
  schedulesSelected,
  isLoadingSchedules
}: CardProps) => {
  const initialCount = 15;
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const hasMoreToShow = (schedulesSelected?.length || 0) > visibleCount;

  const visibleSchedules = (schedulesSelected || []).slice(0, visibleCount);
  const formattedSchedules = formatSchedulesDays(schedulesSelected);

  const loadMoreOrLess = () => {
    if (visibleCount >= (schedulesSelected?.length || 0))
      setVisibleCount(initialCount);
    else setVisibleCount((prev) => prev + initialCount);
  };

  return (
    <>
      {isLoadingSchedules && <Spinner height={80} colorText="#5B36F2" />}

      {!isLoadingSchedules && visibleSchedules.length === 0 && (
        <EmptyDataStateMessage
          message="Por el momento no hay horarios disponibles"
          layout="row"
        />
      )}

      {!isLoadingSchedules && visibleSchedules.length > 0 && (
        <ContainerSubTable>
          <SubTable>
            <SubTableHeader>
              <div style={{ justifyContent: 'start' }}>Sección</div>
              <div style={{ justifyContent: 'start' }}>Horario</div>
              <div style={{ justifyContent: 'start' }}>Docente</div>
              <div style={{ justifyContent: 'start' }}>Modalidad de curso</div>
            </SubTableHeader>
            <SubTableBody>
              <>
                {visibleSchedules.map((schedule, index) => (
                  <ScheduleRow
                    key={`schedule-${schedule.section}-${index}`}
                    schedule={schedule}
                    formatSchedules={formattedSchedules}
                  />
                ))}
                {schedulesSelected?.length > initialCount && (
                  <LoadMoreRow
                    onClick={loadMoreOrLess}
                    hasMore={hasMoreToShow}
                  />
                )}
              </>
            </SubTableBody>
          </SubTable>
        </ContainerSubTable>
      )}
    </>
  );
};

export {
  SubTableSchedules,
  formatSchedulesDays,
  getTypeConfig,
  renderTeachers
};
